/**
 * The expression language art markup is animated with: `rotate="t*20"`,
 * `opacity="0.5+0.5*sin(t*2+i)"`, `fill="mix(accent, #ffffff, hover)"`.
 *
 * Arithmetic, comparison, a ternary, a fixed table of functions and the names a scope hands it -
 * nothing else. There are no strings, no property access and no way to call anything not in
 * `FUNCTIONS`, which is what lets a page document carry one on a public site: the language cannot
 * reach past the numbers and colors it is given.
 *
 * An expression is compiled once, when the markup is read, into a tree of closures. A value that
 * names nothing that changes is folded to a constant there and costs nothing per frame.
 */
import { darken, hex, lighten, mixColor, type Color } from "../../../common/color.js";

export type Value = number | Color;

/** Looks a name up while an expression runs: a parameter, a generator's index, a variable, a palette slot. */
export type Scope = (name: string) => Value | undefined;

export interface Expression {
  /** The source, for messages. */
  readonly source: string;
  evaluate(scope: Scope): Value;
  /** True when it reads nothing but literals and constants. */
  readonly constant: boolean;
  /** Every name it reads, functions excluded. */
  readonly names: ReadonlySet<string>;
}

export class ExpressionError extends Error {
  constructor(
    message: string,
    readonly source: string,
    readonly at: number,
  ) {
    super(`${message} in "${source}"`);
  }
}

const CONSTANTS: Record<string, number> = { pi: Math.PI, tau: Math.PI * 2, e: Math.E, true: 1, false: 0 };

const isColor = (v: Value): v is Color => typeof v === "object";
const num = (v: Value): number => (typeof v === "number" ? v : v.a);
const col = (v: Value): Color => (typeof v === "number" ? { r: v, g: v, b: v, a: 1 } : v);

/** A hash in 0..1 of a few numbers: the same inputs, the same answer, forever. */
export function hashOf(...parts: number[]): number {
  let h = 2166136261 >>> 0;
  for (const p of parts) {
    const bits = Math.floor(p * 1000003) | 0;
    h = Math.imul(h ^ bits, 16777619) >>> 0;
    h = Math.imul(h ^ (h >>> 13), 0x5bd1e995) >>> 0;
  }
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

/** Smooth value noise in 0..1, continuous in `x` (and `y`). */
export function noise(x: number, y = 0): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const ux = fx * fx * (3 - 2 * fx);
  const uy = fy * fy * (3 - 2 * fy);
  const a = hashOf(ix, iy);
  const b = hashOf(ix + 1, iy);
  const c = hashOf(ix, iy + 1);
  const d = hashOf(ix + 1, iy + 1);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}

const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a || 1e-9)));
  return t * t * (3 - 2 * t);
};

type Fn = (args: Value[], scope: Scope) => Value;

/** `[min args, max args, body]`. `rand` reads the scope: its answer belongs to a copy and a seed. */
const FUNCTIONS: Record<string, [number, number, Fn]> = {
  sin: [1, 1, ([x]) => Math.sin(num(x!))],
  cos: [1, 1, ([x]) => Math.cos(num(x!))],
  tan: [1, 1, ([x]) => Math.tan(num(x!))],
  asin: [1, 1, ([x]) => Math.asin(num(x!))],
  acos: [1, 1, ([x]) => Math.acos(num(x!))],
  atan: [1, 2, (a) => (a.length === 2 ? Math.atan2(num(a[0]!), num(a[1]!)) : Math.atan(num(a[0]!)))],
  abs: [1, 1, ([x]) => Math.abs(num(x!))],
  sign: [1, 1, ([x]) => Math.sign(num(x!))],
  floor: [1, 1, ([x]) => Math.floor(num(x!))],
  ceil: [1, 1, ([x]) => Math.ceil(num(x!))],
  round: [1, 1, ([x]) => Math.round(num(x!))],
  fract: [1, 1, ([x]) => num(x!) - Math.floor(num(x!))],
  sqrt: [1, 1, ([x]) => Math.sqrt(Math.max(0, num(x!)))],
  exp: [1, 1, ([x]) => Math.exp(num(x!))],
  log: [1, 1, ([x]) => Math.log(num(x!))],
  pow: [2, 2, ([x, y]) => Math.pow(num(x!), num(y!))],
  mod: [2, 2, ([x, y]) => {
    const m = num(y!);
    return m === 0 ? 0 : ((num(x!) % m) + m) % m;
  }],
  min: [1, 16, (a) => Math.min(...a.map(num))],
  max: [1, 16, (a) => Math.max(...a.map(num))],
  clamp: [1, 3, (a) => Math.min(a.length > 2 ? num(a[2]!) : 1, Math.max(a.length > 1 ? num(a[1]!) : 0, num(a[0]!)))],
  mix: [3, 3, ([a, b, k]) =>
    isColor(a!) || isColor(b!) ? mixColor(col(a!), col(b!), num(k!)) : num(a!) + (num(b!) - num(a!)) * num(k!)],
  step: [2, 2, ([edge, x]) => (num(x!) >= num(edge!) ? 1 : 0)],
  smooth: [1, 3, (a) => (a.length === 3 ? smoothstep(num(a[0]!), num(a[1]!), num(a[2]!)) : smoothstep(0, 1, num(a[0]!)))],
  /** 0..1 and back, once a second: a sine raised off zero. */
  wave: [1, 1, ([x]) => 0.5 + 0.5 * Math.sin(num(x!) * Math.PI * 2)],
  /** 0 to 1 and back to 0, linearly, once per unit. */
  tri: [1, 1, ([x]) => {
    const f = num(x!) - Math.floor(num(x!));
    return 1 - Math.abs(f * 2 - 1);
  }],
  /** The fraction of the way through a cycle `x` has come, of `period` (1 by default). */
  saw: [1, 2, (a) => {
    const p = a.length > 1 ? num(a[1]!) || 1 : 1;
    const v = num(a[0]!) / p;
    return v - Math.floor(v);
  }],
  /** A short spike at the start of every `period`, falling off over `width` of it. */
  pulse: [1, 3, (a) => {
    const p = a.length > 1 ? num(a[1]!) || 1 : 1;
    const w = a.length > 2 ? Math.max(1e-4, num(a[2]!)) : 0.15;
    const f = num(a[0]!) / p - Math.floor(num(a[0]!) / p);
    return Math.max(0, 1 - f / w);
  }],
  rad: [1, 1, ([x]) => (num(x!) * Math.PI) / 180],
  deg: [1, 1, ([x]) => (num(x!) * 180) / Math.PI],
  hypot: [2, 2, ([x, y]) => Math.hypot(num(x!), num(y!))],
  /** Fixed per copy and per seed: `rand(1)` and `rand(2)` are two independent numbers. */
  rand: [0, 1, (a, scope) => {
    const seed = scope("seed");
    // The copy a generator is drawing, nesting included; a bare index where there is none.
    const copy = scope("__copy") ?? scope("i");
    return hashOf(typeof seed === "number" ? seed : 0, typeof copy === "number" ? copy : 0, a.length ? num(a[0]!) : 0, 7.31);
  }],
  hash: [1, 4, (a) => hashOf(...a.map(num))],
  noise: [1, 2, (a) => noise(num(a[0]!), a.length > 1 ? num(a[1]!) : 0)],
  rgb: [3, 4, (a) => ({ r: num(a[0]!), g: num(a[1]!), b: num(a[2]!), a: a.length > 3 ? num(a[3]!) : 1 })],
  rgba: [4, 4, (a) => ({ r: num(a[0]!), g: num(a[1]!), b: num(a[2]!), a: num(a[3]!) })],
  /** A color at a different opacity: `alpha(accent, 0.4)`. Multiplies, as `fade` does. */
  alpha: [2, 2, ([c, k]) => {
    const v = col(c!);
    return { r: v.r, g: v.g, b: v.b, a: v.a * num(k!) };
  }],
  mixc: [3, 3, ([a, b, k]) => mixColor(col(a!), col(b!), num(k!))],
  lighten: [2, 2, ([c, k]) => lighten(col(c!), num(k!))],
  darken: [2, 2, ([c, k]) => darken(col(c!), num(k!))],
  /** A fire's hot core: the color pushed toward white. */
  spark: [1, 1, ([c]) => mixColor(lighten(col(c!), 0.35), { r: 1, g: 0.95, b: 0.8, a: col(c!).a }, 0.35)],
};

export const FUNCTION_NAMES: readonly string[] = Object.keys(FUNCTIONS);

type Node = (scope: Scope) => Value;

interface Token {
  kind: "num" | "name" | "color" | "op" | "end";
  text: string;
  value?: Value;
  at: number;
}

const WORDS: Record<string, string> = { and: "&&", or: "||", not: "!" };

const OPS = ["<=", ">=", "==", "!=", "&&", "||", "+", "-", "*", "/", "%", "^", "<", ">", "!", "?", ":", "(", ")", ","];

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const c = source[i]!;
    if (c === " " || c === "\t" || c === "\n" || c === "\r") {
      i++;
      continue;
    }
    if ((c >= "0" && c <= "9") || (c === "." && /[0-9]/.test(source[i + 1] ?? ""))) {
      const m = /^(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/i.exec(source.slice(i))!;
      tokens.push({ kind: "num", text: m[0], value: Number(m[0]), at: i });
      i += m[0].length;
      continue;
    }
    if (c === "#") {
      const m = /^#[0-9a-f]+/i.exec(source.slice(i));
      if (!m || ![4, 5, 7, 9].includes(m[0].length)) throw new ExpressionError("a color is #rgb, #rgba, #rrggbb or #rrggbbaa", source, i);
      tokens.push({ kind: "color", text: m[0], value: hex(m[0]), at: i });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const text = /^[A-Za-z_][A-Za-z0-9_]*/.exec(source.slice(i))![0];
      // Words for the operators XML will not have in an attribute: `&` must be written `&amp;`.
      const word = Object.hasOwn(WORDS, text) ? WORDS[text] : undefined;
      tokens.push(word ? { kind: "op", text: word, at: i } : { kind: "name", text, at: i });
      i += text.length;
      continue;
    }
    const op = OPS.find((o) => source.startsWith(o, i));
    if (!op) throw new ExpressionError(`"${c}" is not part of an expression`, source, i);
    tokens.push({ kind: "op", text: op, at: i });
    i += op.length;
  }
  tokens.push({ kind: "end", text: "", at: source.length });
  return tokens;
}

/** Compile `source` once, to be evaluated every frame. Throws `ExpressionError` on anything it cannot read. */
export function compileExpression(source: string): Expression {
  const tokens = tokenize(source);
  let p = 0;
  const names = new Set<string>();
  let reads = false;
  const peek = (): Token => tokens[p]!;
  const take = (): Token => tokens[p++]!;
  const accept = (text: string): boolean => {
    if (peek().kind === "op" && peek().text === text) {
      p++;
      return true;
    }
    return false;
  };
  const expect = (text: string): void => {
    if (!accept(text)) throw new ExpressionError(`expected "${text}"`, source, peek().at);
  };

  const binary = (a: Node, b: Node, op: string): Node => {
    switch (op) {
      case "+": return (s) => num(a(s)) + num(b(s));
      case "-": return (s) => num(a(s)) - num(b(s));
      case "*": return (s) => num(a(s)) * num(b(s));
      case "/": return (s) => {
        const d = num(b(s));
        return d === 0 ? 0 : num(a(s)) / d;
      };
      case "%": return (s) => {
        const d = num(b(s));
        return d === 0 ? 0 : num(a(s)) % d;
      };
      case "^": return (s) => Math.pow(num(a(s)), num(b(s)));
      case "<": return (s) => (num(a(s)) < num(b(s)) ? 1 : 0);
      case ">": return (s) => (num(a(s)) > num(b(s)) ? 1 : 0);
      case "<=": return (s) => (num(a(s)) <= num(b(s)) ? 1 : 0);
      case ">=": return (s) => (num(a(s)) >= num(b(s)) ? 1 : 0);
      case "==": return (s) => (num(a(s)) === num(b(s)) ? 1 : 0);
      case "!=": return (s) => (num(a(s)) !== num(b(s)) ? 1 : 0);
      case "&&": return (s) => (num(a(s)) !== 0 && num(b(s)) !== 0 ? 1 : 0);
      default: return (s) => (num(a(s)) !== 0 || num(b(s)) !== 0 ? 1 : 0);
    }
  };

  const level = (ops: readonly string[], next: () => Node) => (): Node => {
    let left = next();
    for (;;) {
      const t = peek();
      if (t.kind !== "op" || !ops.includes(t.text)) return left;
      p++;
      left = binary(left, next(), t.text);
    }
  };

  const expression = (): Node => {
    const cond = or();
    if (!accept("?")) return cond;
    const yes = expression();
    expect(":");
    const no = expression();
    return (s) => (num(cond(s)) !== 0 ? yes(s) : no(s));
  };
  const unary = (): Node => {
    if (accept("-")) {
      const v = unary();
      return (s) => -num(v(s));
    }
    if (accept("+")) return unary();
    if (accept("!")) {
      const v = unary();
      return (s) => (num(v(s)) === 0 ? 1 : 0);
    }
    return power();
  };
  const power = (): Node => {
    const base = primary();
    if (!accept("^")) return base;
    return binary(base, unary(), "^");
  };
  const mul = level(["*", "/", "%"], unary);
  const add = level(["+", "-"], mul);
  const cmp = level(["<", ">", "<=", ">=", "==", "!="], add);
  const and = level(["&&"], cmp);
  const or = level(["||"], and);

  function primary(): Node {
    const t = take();
    if (t.kind === "num" || t.kind === "color") {
      const v = t.value!;
      return () => v;
    }
    if (t.kind === "op" && t.text === "(") {
      const inner = expression();
      expect(")");
      return inner;
    }
    if (t.kind === "name") {
      if (accept("(")) {
        const fn = Object.hasOwn(FUNCTIONS, t.text) ? FUNCTIONS[t.text] : undefined;
        if (!fn) throw new ExpressionError(`there is no function "${t.text}"`, source, t.at);
        const args: Node[] = [];
        if (!accept(")")) {
          do args.push(expression());
          while (accept(","));
          expect(")");
        }
        const [lo, hi, body] = fn;
        if (args.length < lo || args.length > hi) {
          throw new ExpressionError(
            `${t.text} takes ${lo === hi ? lo : `${lo} to ${hi}`} argument${hi === 1 ? "" : "s"}`,
            source,
            t.at,
          );
        }
        if (t.text === "rand") reads = true;
        return (s) => body(args.map((a) => a(s)), s);
      }
      const constant = Object.hasOwn(CONSTANTS, t.text) ? CONSTANTS[t.text] : undefined;
      if (constant !== undefined) return () => constant;
      reads = true;
      names.add(t.text);
      const name = t.text;
      // Only a number or a color comes back in: a scope built over a plain object would otherwise
      // hand an expression whatever `constructor` or `toString` happen to be.
      return (s) => {
        const v = s(name);
        return typeof v === "number" ? v : v && typeof v === "object" && typeof v.r === "number" ? v : 0;
      };
    }
    throw new ExpressionError(t.kind === "end" ? "the expression ends too soon" : `"${t.text}" was not expected here`, source, t.at);
  }

  if (source.trim() === "") throw new ExpressionError("the expression is empty", source, 0);
  let root = expression();
  if (peek().kind !== "end") throw new ExpressionError(`"${peek().text}" was not expected here`, source, peek().at);

  if (!reads) {
    const value = root(() => undefined);
    root = () => value;
  }
  return { source, evaluate: root, constant: !reads, names };
}

/** A number out of an expression's value; a color counts as its alpha, which is what a mask reads. */
export const toNumber = num;

/** A color out of an expression's value; a number is a gray. */
export const toColor = col;

export { isColor };
